<template>
  <ListItem
    :to="{
      name: 'invoice',
      params: { id: invoice.id },
    }"
    :variant="variant"
    :drop-down-items="dropDownItems"
  >
    <div
      class="flex basis-[35%] flex-col justify-center self-stretch rounded-sm bg-white p-1 text-center md:basis-[15%]"
    >
      <UserListItemTitle>
        {{ statusText }}
      </UserListItemTitle>
      <div class="text-base font-semibold">{{ invoice.completeInvoiceNumber }}</div>
    </div>

    <div class="flex min-w-0 flex-1 basis-[65%] gap-1 max-sm:flex-col md:basis-[85%] md:gap-4">
      <div class="basis-[40%] space-y-1">
        <p>{{ invoiceDate }}</p>
        <p class="font-semibold"><span v-currency="invoice.totalPositionSum" /></p>
      </div>

      <div class="basis-[60%] space-y-1">
        <p>
          {{ invoice.receiverData.firstName }}
          {{ invoice.receiverData.lastName }}
        </p>
        <p>
          {{ t('booking') }} <span class="text-obsidian-400"># </span
          >{{ invoice.booking ? invoice.booking.bookingNumber : 'n/a' }}
        </p>
      </div>
    </div>
  </ListItem>
</template>

<script setup lang="ts">
import type { Invoice } from '@/entities/invoices/invoice.entity';
import { useFormattedDate } from '@/hooks/use-formatted-date';
import { useI18n } from 'vue-i18n';
import { DateTime } from 'luxon';
import { computed, ref, watch } from 'vue';
import type { DropdownItem } from '@/components/DropdownDotted.vue';
import { InvoiceStatus } from '@/entities/invoices/invoice-status.enum';
import { useInvoicePdfs } from '@/hooks/use-invoice-pdf';
import { Alert } from '@/services/alert.service';
import { useUpdateInvoice } from '@/queries/use-invoices';
import { InvoiceType } from '@/entities/invoices/invoice-type.enum';
import ListItem, { type ListItemVariant } from '@/components/ListItem.vue';
import UserListItemTitle from '@/views/users/user-list/UserListItemTitle.vue';
import { isZero } from '@/utils/is-zero';

const props = defineProps<{
  invoice: Invoice;
}>();

const { t } = useI18n();

const invoiceDate = useFormattedDate(
  computed(() => props.invoice.invoiceDate),
  DateTime.DATE_MED,
);

const variant = ref<ListItemVariant>('default');
const statusText = ref('');

watch(
  props.invoice,
  () => {
    if (props.invoice.status === InvoiceStatus.CANCELED) {
      variant.value = 'warning';
      statusText.value = t('canceled');
      return;
    }
    if (props.invoice.type === InvoiceType.REVERSAL) {
      variant.value = 'info';
      statusText.value = t('reversal');
      return;
    }
    if (isZero(props.invoice.currentBalance)) {
      variant.value = 'success';
      statusText.value = t('paid');
      return;
    }
    if (
      props.invoice.invoiceDate &&
      -DateTime.fromISO(props.invoice.invoiceDate).startOf('day').diffNow('days').as('days') < 14
    ) {
      variant.value = 'problem';
      statusText.value = t('notPaid');
      return;
    }
    variant.value = 'danger';
    statusText.value = t('overdue');
  },
  { immediate: true, deep: true },
);

const { mutateAsync: updateInvoice } = useUpdateInvoice();

const { downloadInvoice, downloadInvoiceDirect, sendInvoiceToCustomerWithAlert } = useInvoicePdfs();

const cancelInvoice = async (invoiceId: string) => {
  const alertResult = await Alert.fire({
    titleText: t('reallyCancelInvoiceTitle'),
    text: t('reallyCancelInvoiceText'),
    icon: 'warning',
    showDenyButton: true,
    confirmButtonText: t('cancelInvoice'),
    denyButtonText: t('dontCancel'),
  });
  if (alertResult.isConfirmed) {
    updateInvoice({
      id: invoiceId,
      invoice: { status: InvoiceStatus.CANCELED },
    });
  }
};

const dropDownItems = computed<DropdownItem[]>(() => {
  return [
    {
      onClick: () => downloadInvoice(props.invoice),
      title: t('download'),
    },
    {
      onClick: () => downloadInvoiceDirect(props.invoice),
      title: t('downloadDirect'),
    },
    {
      onClick: () => sendInvoiceToCustomerWithAlert(props.invoice.id),
      title: t('sendToCustomer'),
    },
    ...(props.invoice.status === InvoiceStatus.CANCELED
      ? []
      : [
          {
            title: t('cancelInvoice'),
            onClick: () => cancelInvoice(props.invoice.id),
          },
        ]),
  ];
});
</script>

<i18n lang="json">
{
  "en": {
    "date": "Date",
    "invoiceNumber": "INr.",
    "customer": "Customer",
    "bookingNumber": "Bnr.",
    "total": "Total",
    "cancelInvoice": "Cancel Invoice",
    "reallyCancelInvoiceTitle": "Really cancel Invoice?",
    "reallyCancelInvoiceText": "The Invoice will be permanently canceled.",
    "dontCancel": "Don't cancel",
    "download": "Show PDF",
    "downloadDirect": "Download PDF",
    "sendToCustomer": "Send to Customer",
    "resendConfirmEmail": "Resend Booking Confirmation",
    "canceled": "Canceled",
    "reversal": "Reversal",
    "paid": "Paid",
    "notPaid": "Not paid",
    "overdue": "Overdue",
    "booking": "Booking"
  },
  "de": {
    "date": "Datum",
    "invoiceNumber": "RNr.",
    "customer": "Kunde",
    "bookingNumber": "Bnr.",
    "total": "Betrag",
    "cancelInvoice": "Rechnung stornieren",
    "reallyCancelInvoiceTitle": "Rechnung wirklich stornieren?",
    "reallyCancelInvoiceText": "Die Rechnung wird unwiderruflich storniert.",
    "dontCancel": "Nicht stornieren",
    "download": "Anzeigen (PDF)",
    "downloadDirect": "Herunterladen (PDF)",
    "sendToCustomer": "An Kunde senden",
    "resendConfirmEmail": "Buchungsbestätigung erneut senden",
    "canceled": "Storniert",
    "reversal": "Storno",
    "paid": "Bezahlt",
    "notPaid": "Unbezahlt",
    "overdue": "Überfällig",
    "booking": "Buchung"
  },
  "es": {
    "date": "Fecha",
    "invoiceNumber": "N.º fact.",
    "customer": "Cliente",
    "bookingNumber": "N.º res.",
    "total": "Importe",
    "cancelInvoice": "Anular factura",
    "reallyCancelInvoiceTitle": "¿Seguro que desea anular la factura?",
    "reallyCancelInvoiceText": "La factura se anulará de forma definitiva.",
    "dontCancel": "No anular",
    "download": "Ver (PDF)",
    "downloadDirect": "Descargar (PDF)",
    "sendToCustomer": "Enviar al cliente",
    "resendConfirmEmail": "Reenviar confirmación de reserva",
    "canceled": "Anulada",
    "reversal": "Abono",
    "paid": "Pagada",
    "notPaid": "No pagada",
    "overdue": "Vencida",
    "booking": "Reserva"
  }
}
</i18n>
